import { X, CheckCircle, WarningCircle, Trash, SpinnerGap } from '@phosphor-icons/react';
import { useNotifications } from '../../contexts/NotificationContext';

export function NotificationPanel() {
  const { tasks, isPanelOpen, closePanel, removeTask, clearCompleted } = useNotifications();

  if (!isPanelOpen) return null;

  const hasCompleted = tasks.some(t => t.status !== 'processing');

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/20 backdrop-blur-[2px] z-50 transition-opacity"
        onClick={closePanel}
      />
      
      {/* Slide-out Panel */}
      <div className="fixed top-0 right-0 h-screen w-full max-w-sm bg-white shadow-2xl z-50 flex flex-col font-sans animate-in slide-in-from-right duration-300">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <h2 className="text-lg font-outfit font-extrabold text-slate-800">Notifications</h2>
          <div className="flex items-center gap-2">
            {hasCompleted && (
              <button 
                onClick={clearCompleted}
                className="text-[10px] font-bold text-slate-400 hover:text-slate-600 uppercase tracking-wider px-2 py-1 transition-colors"
              >
                Clear
              </button>
            )}
            <button 
              onClick={closePanel}
              className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>

        {/* Task List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <p className="text-sm font-semibold">No recent notifications</p>
            </div>
          ) : (
            tasks.map(task => (
              <div 
                key={task.id} 
                className={`border rounded-xl p-4 transition-colors ${
                  task.status === 'error' ? 'bg-rose-50/30 border-rose-100' : 'bg-white border-slate-100 shadow-sm'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    {task.status === 'processing' && (
                      <SpinnerGap size={18} className="animate-spin text-accent" weight="bold" />
                    )}
                    {task.status === 'completed' && (
                      <CheckCircle size={18} weight="fill" className="text-emerald-500" />
                    )}
                    {task.status === 'error' && (
                      <WarningCircle size={18} weight="fill" className="text-rose-500" />
                    )}
                    <h4 className="text-xs font-bold text-slate-800 truncate max-w-[200px]">
                      {task.fileName}
                    </h4>
                  </div>
                  {task.status !== 'processing' && (
                    <button 
                      onClick={() => removeTask(task.id)}
                      className="text-slate-400 hover:text-rose-500 transition-colors"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </div>

                {/* Processing State */}
                {task.status === 'processing' && (
                  <div>
                    <div className="flex justify-between text-[10px] font-bold text-slate-500 mb-1.5 uppercase tracking-wider">
                      <span>Processing</span>
                      <span>{task.progress.current} / {task.progress.total}</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-accent transition-all duration-300 ease-out"
                        style={{ width: task.progress.total > 0 ? `${(task.progress.current / task.progress.total) * 100}%` : '5%' }}
                      />
                    </div>
                  </div>
                )}

                {/* Completed State */}
                {task.status === 'completed' && task.summary && (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div className="bg-emerald-50/50 border border-emerald-100 rounded-lg py-1.5 flex flex-col items-center">
                      <span className="text-sm font-black text-emerald-600">{task.summary.inserted}</span>
                      <span className="text-[9px] font-bold text-emerald-600/80 uppercase tracking-wider">Inserted</span>
                    </div>
                    <div className="bg-blue-50/50 border border-blue-100 rounded-lg py-1.5 flex flex-col items-center">
                      <span className="text-sm font-black text-blue-600">{task.summary.updated}</span>
                      <span className="text-[9px] font-bold text-blue-600/80 uppercase tracking-wider">Updated</span>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg py-1.5 flex flex-col items-center">
                      <span className="text-sm font-black text-slate-600">{task.summary.skipped}</span>
                      <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Skipped</span>
                    </div>
                  </div>
                )}

                {/* Error State */}
                {task.status === 'error' && (
                  <p className="text-xs font-semibold text-rose-600 bg-rose-50 p-2 rounded-lg mt-2">
                    {task.errorMsg || 'Upload failed'}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
